import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import useThemeColors from "./useThemeColors";

const MAX_CREATORS = 8;

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-theme-secondary border border-theme-secondary rounded px-3 py-2 shadow-lg text-xs text-theme-text">
      <div className="font-medium">{d.name}</div>
      <div className="opacity-70 mt-0.5">{d.count} video{d.count !== 1 ? "s" : ""}</div>
    </div>
  );
};

const CreatorConcentration = ({ videoRows }) => {
  const colors = useThemeColors();

  const data = useMemo(() => {
    const counts = {};
    videoRows.forEach((r) => {
      const name = r.creator?.name || r.creator?.platform_id || "Unknown";
      counts[name] = (counts[name] || 0) + 1;
    });
    const sorted = Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    if (sorted.length <= MAX_CREATORS) return sorted;

    const top = sorted.slice(0, MAX_CREATORS - 1);
    const rest = sorted.slice(MAX_CREATORS - 1);
    const otherCount = rest.reduce((s, r) => s + r.count, 0);
    return [...top, { name: `${rest.length} others`, count: otherCount, isOther: true }];
  }, [videoRows]);

  if (data.length < 2) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-theme-text mb-2">Creator Spread</h4>
        <div className="flex items-center justify-center h-32 text-theme-text opacity-50 text-sm">
          {data.length === 0 ? "No video data." : "All videos from one creator."}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-semibold text-theme-text mb-2">Creator Spread</h4>
      <ResponsiveContainer width="100%" height={Math.max(140, data.length * 28 + 16)}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: colors.text, fillOpacity: 0.5 }}
            stroke={colors.shadow}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={100}
            tick={{ fontSize: 11, fill: colors.text, fillOpacity: 0.7 }}
            stroke="none"
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: colors.shadow, fillOpacity: 0.3 }} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={20}>
            {data.map((entry, i) => (
              <Cell
                key={i}
                fill={entry.isOther ? colors.shadow : colors.primary}
                fillOpacity={entry.isOther ? 0.6 : 0.7}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CreatorConcentration;
